export interface RatingDimension {
  key: string;
  label: string;
  description: string;
}

export interface ResolvedRatingEntry extends RatingDimension {
  value: number;
  configured: boolean;
}

type RatingItemKind = 'lesson';
export type RatingOverrideMap = Readonly<
  Record<string, Partial<Record<RatingItemKind, readonly RatingDimension[]>>>
>;

const DEFAULT_DIMENSIONS: Record<RatingItemKind, readonly RatingDimension[]> = {
  lesson: [
    {
      key: 'academic_accuracy',
      label: 'Academic accuracy',
      description:
        'Source fidelity, plus the academic accuracy of everything added on top of the source — enriched explanations, examples, and invented Socratic steps.',
    },
    {
      key: 'engagement',
      label: 'Engagement',
      description:
        'How engaging the lesson is for a learner — flow, question quality, and whether the lesson keeps the learner actively thinking rather than passively reading.',
    },
  ],
};

// Intentionally empty in v1. A course can replace the complete dimension list without changing
// stored ratings or the API contract.
const COURSE_OVERRIDES: RatingOverrideMap = {};

export function getRatingDimensions(courseKey: string, itemKind: RatingItemKind): RatingDimension[] {
  return resolveRatingDimensions(courseKey, itemKind, COURSE_OVERRIDES);
}

export function resolveRatingDimensions(
  courseKey: string,
  itemKind: RatingItemKind,
  overrides: RatingOverrideMap,
): RatingDimension[] {
  return [...(overrides[courseKey]?.[itemKind] ?? DEFAULT_DIMENSIONS[itemKind])];
}

/** Current dimensions first, then self-described historical keys no longer in configuration. */
export function resolveStoredRatings(
  courseKey: string,
  itemKind: RatingItemKind,
  ratings: Readonly<Record<string, number>>,
): ResolvedRatingEntry[] {
  const configured = getRatingDimensions(courseKey, itemKind);
  const configuredKeys = new Set(configured.map((dimension) => dimension.key));
  const current = configured.flatMap((dimension) => {
    const value = ratings[dimension.key];
    return value === undefined ? [] : [{ ...dimension, value, configured: true }];
  });
  const historical = Object.entries(ratings).flatMap(([key, value]) =>
    configuredKeys.has(key)
      ? []
      : [{
          key,
          label: humanizeRatingKey(key),
          description: 'This historical rating dimension is not in the current course configuration.',
          value,
          configured: false,
        }],
  );
  return [...current, ...historical];
}

export function humanizeRatingKey(key: string): string {
  const words = key.replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Single source of truth for the future approval gate. Deliberately off for every course in v1. */
export function enforceRatingsBeforeApprove(_courseKey: string): boolean {
  return false;
}
